import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import useBoothStatusButton from '~/features/circle-participation/hooks/useBoothStatusButton'
import { type CircleApplyDetailResponse } from '~/features/circle-participation/types/circle-apply'
import {
  getBoothStatusColor,
  getBoothStatusText,
} from '~/features/circle-participation/utils/function'
import Round from '~/shared/components/round/Round'
import Tag from '~/shared/components/tags/Tag'
import { isModalOpenState, selectedApplicationIdState } from '~/shared/states'
import theme from '~/shared/styles/theme'

type Props = {
  data: CircleApplyDetailResponse
}

function CircleRequiredInfo({ data }: Props) {
  const navigate = useNavigate()
  const {
    id,
    booth_no,
    booth_status,
    booth_name,
    date_type,
    booth_type,
    booth_option_type,
    zone_type,
    user_name,
    user_email,
    user_mobile,
    etc,
    created_at,
  } = data.userBoothApplication

  const [_selectedApplicationId, setSelectedApplicationId] = useRecoilState(
    selectedApplicationIdState,
  )

  const [isModalOpen, setIsModalOpen] = useRecoilState(isModalOpenState)

  const { modify, cancel, refund } = useBoothStatusButton(booth_status)

  const { date_type_list, booth_type_list, zone_type_list } =
    data.userBoothApplication

  const filteredDateType = date_type_list.find(
    (item) => item.code === date_type,
  )?.description

  const filteredBoothType = booth_type_list.find(
    (item) => item.code === booth_type,
  )?.description

  const filteredBoothOptionType = data.boothApplication.booth_type_list
    .filter((item) => item.is_option === 'Y')
    .find((item) => item.code === booth_option_type)

  const boothDescription =
    filteredBoothOptionType?.booth_description?.replace(/\/\/.*/, '') ?? '-'

  const boothOptionText = filteredBoothOptionType
    ? ` * ${filteredBoothOptionType.description} ${boothDescription} 희망`
    : ''

  const filteredZoneType = zone_type_list.find(
    (item) => item.code === zone_type,
  )?.description

  const requestCancel = () => {
    if (id) {
      setSelectedApplicationId(id.toString())
      setIsModalOpen({
        ...isModalOpen,
        contents: 'circle-apply-cancel-confirm',
        isOpen: true,
      })
    }
  }

  const handleClickEdit = () => {
    navigate(`/circle-participation/edit/${id}`)
  }

  return (
    <Wrapper>
      <SubTitleBox>
        <SubTitle>서클 정보</SubTitle>
        <ButtonBox>
          {modify && <Button onClick={handleClickEdit}>신청서 수정</Button>}
          {cancel && <Button onClick={requestCancel}>신청서 취소</Button>}
          {refund && <Button>환불 요청</Button>}
        </ButtonBox>
      </SubTitleBox>
      <TagContainer>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            신청 상태
          </Tag>
          <StatusBox>
            <Round color={getBoothStatusColor(booth_status)} />
            <StatusText $color={getBoothStatusColor(booth_status)}>
              {getBoothStatusText(booth_status) || '-'}
            </StatusText>
          </StatusBox>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            서클 배치번호
          </Tag>
          <TagText>{booth_no || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            서클 이름
          </Tag>
          <TagText>{booth_name || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            신청일
          </Tag>
          <TagText>{created_at || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            참가 희망일
          </Tag>
          <TagText>{filteredDateType || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            서클 참가 사양
          </Tag>
          <TagBox>
            <TagText>
              {filteredBoothType || '-'}
              <span className="accent">{boothOptionText}</span>
            </TagText>
          </TagBox>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            특별존 참가
          </Tag>
          <TagText>{filteredZoneType || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            대표자 본명
          </Tag>
          <TagText>{user_name || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            이메일
          </Tag>
          <TagText>{user_email || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            대표자 연락처
          </Tag>
          <TagText>{user_mobile || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            비고
          </Tag>
          <TagText>{etc || '-'}</TagText>
        </TagBox>
      </TagContainer>
    </Wrapper>
  )
}

export default CircleRequiredInfo

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: flex-start;

  box-sizing: border-box;
  width: 100%;
  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  @media ${theme.device.medium} {
    padding: 24px 20px;
  }
`

const SubTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const SubTitle = styled.h2`
  width: 100%;
  ${theme.fontStyle.subTitle};

  @media ${theme.device.medium} {
    ${theme.fontStyle.subTitleMobile};
    width: 100%;
    height: auto;
  }
`

const ButtonBox = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  @media ${theme.device.large} {
    flex-direction: row;
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
  }
`

const Button = styled.button`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  height: 58px;
  padding: 0 20px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: white;
  word-wrap: break-word;
  white-space: nowrap;

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 54px;

  @media ${theme.device.large} {
    width: auto;
  }

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;

  .desc {
    width: 100%;
    ${theme.fontStyle.text};
    color: #313634;
    white-space: pre-wrap;
  }

  .accent {
    color: ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const StatusBox = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StatusText = styled.span<{ $color: string }>`
  ${theme.fontStyle.text};
  font-family: NanumSquareNeo-ExtraBold, sans-serif;
  color: ${(props) => props.$color};

  @media ${theme.device.medium} {
    ${theme.fontStyle.textMobile};
  }
`

const TagText = styled.span`
  ${theme.fontStyle.text};
  color: #313634;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    ${theme.fontStyle.textMobile};
  }
`
